/**
 * Site Analytics Dashboard
 * Displays native analytics data for a site
 */

import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  BarChart2,
  TrendingUp,
  TrendingDown,
  Users,
  ArrowLeft,
  Calendar,
  ChevronDown,
  Clock,
  Monitor,
  Smartphone,
  Tablet,
  Layers,
  Globe,
  Chrome,
} from 'lucide-react';
import { cn } from '../../utils/cn';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { analyticsApi } from '../../api/modules/analytics';
import { NavigationFlow, pickSankeyStartPage } from './NavigationFlow';
import { sitesApi } from '../../api/modules/sites';
import type {
  AnalyticsOverview,
  AnalyticsTimelinePoint,
  AnalyticsPageStats,
  AnalyticsReferrerStats,
  AnalyticsDeviceStats,
  AnalyticsBrowserStats,
  AnalyticsScreenStats,
  AnalyticsLandingPageStats,
  AnalyticsDateRange,
  SankeyData,
} from '../../api/modules/analytics';

// Color palette for charts
const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

// Hook for responsive design
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
  );

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [breakpoint]);

  return isMobile;
}

// Date range preset options
const DATE_RANGE_PRESETS = [
  { label: '7 Days', days: 7 },
  { label: '30 Days', days: 30 },
  { label: '90 Days', days: 90 },
];

function sankeyRequestOptions(showExits: boolean, expanded: boolean, startPage: string | null) {
  return {
    showExits,
    expanded,
    startPage: startPage || undefined,
    // Two steps from the focused page: that page, where people go next, and one step after.
    maxDepth: 2,
    minTransitions: 1,
    minPct: 0,
  };
}

// Type for date range selection
type DateRangeType = '7' | '30' | '90' | 'custom';

// Format date range for display
function formatDateRangeLabel(
  rangeType: DateRangeType,
  customRange: { start: string; end: string }
): string {
  if (rangeType === 'custom' && customRange.start && customRange.end) {
    const start = new Date(customRange.start);
    const end = new Date(customRange.end);
    return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  }

  const days = parseInt(rangeType);
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - days);
  return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
}

function formatNumber(num: number): string {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
}

function toCount(value: number | string | null | undefined): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function formatPercent(rate: number | null): string {
  if (rate === null) return 'N/A';
  return `${(rate * 100).toFixed(1)}%`;
}

function formatAvgPages(avg: number | null): string {
  if (avg === null) return 'N/A';
  return avg.toFixed(1);
}

function formatDuration(seconds: number | null): string {
  if (seconds === null) return 'N/A';
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function getDateRange(days: number): AnalyticsDateRange {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - days);

  return {
    startDate: startDate.toISOString().split('T')[0],
    endDate: endDate.toISOString().split('T')[0],
  };
}

// KPI Card Component
interface KPICardProps {
  title: string;
  value: string | number;
  previousValue?: number;
  currentValue?: number;
  icon: React.ReactNode;
  isRate?: boolean;
}

function KPICard({ title, value, previousValue, currentValue, icon, isRate }: KPICardProps) {
  let change: number | null = null;
  let isPositive = true;

  if (previousValue !== undefined && currentValue !== undefined && previousValue > 0) {
    change = ((currentValue - previousValue) / previousValue) * 100;
    // For exit rate, lower is better
    isPositive = isRate ? change < 0 : change > 0;
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-500">{title}</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
          {change !== null && (
            <p
              className={`text-sm mt-2 flex items-center ${
                isPositive ? 'text-green-600' : 'text-red-600'
              }`}
            >
              {isPositive ? (
                <TrendingUp className="w-4 h-4 mr-1" />
              ) : (
                <TrendingDown className="w-4 h-4 mr-1" />
              )}
              {Math.abs(change).toFixed(1)}% vs previous period
            </p>
          )}
        </div>
        <div className="p-3 bg-blue-100 rounded-full">{icon}</div>
      </div>
    </div>
  );
}

// Device Icon Component
function DeviceIcon({ type }: { type: string }) {
  switch (type.toLowerCase()) {
    case 'mobile':
      return <Smartphone className="w-4 h-4" />;
    case 'tablet':
      return <Tablet className="w-4 h-4" />;
    default:
      return <Monitor className="w-4 h-4" />;
  }
}

export const SiteAnalytics: React.FC = () => {
  const { siteId } = useParams<{ siteId: string }>();
  const [siteName, setSiteName] = useState<string>('');
  const [dateRangeType, setDateRangeType] = useState<DateRangeType>('30');
  const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isMobile = useIsMobile();

  // Data state
  const [overview, setOverview] = useState<AnalyticsOverview | null>(null);
  const [timeline, setTimeline] = useState<AnalyticsTimelinePoint[]>([]);
  const [pages, setPages] = useState<AnalyticsPageStats[]>([]);
  const [referrers, setReferrers] = useState<AnalyticsReferrerStats[]>([]);
  const [devices, setDevices] = useState<AnalyticsDeviceStats[]>([]);
  const [browsers, setBrowsers] = useState<AnalyticsBrowserStats[]>([]);
  const [screens, setScreens] = useState<AnalyticsScreenStats[]>([]);
  const [landingPages, setLandingPages] = useState<AnalyticsLandingPageStats[]>([]);
  const [sankeyData, setSankeyData] = useState<SankeyData | null>(null);
  const [showExits, setShowExits] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [startPage, setStartPage] = useState<string | null>(null);
  const [defaultStartPage, setDefaultStartPage] = useState<string | null>(null);

  // Get the current date range based on type
  const getCurrentDateRange = (): AnalyticsDateRange => {
    if (dateRangeType === 'custom' && customDateRange.start && customDateRange.end) {
      return {
        startDate: customDateRange.start,
        endDate: customDateRange.end,
      };
    }
    return getDateRange(parseInt(dateRangeType));
  };

  // Fetch data
  useEffect(() => {
    async function fetchData() {
      if (!siteId) return;

      // For custom range, only fetch if we have valid dates
      if (dateRangeType === 'custom' && (!customDateRange.start || !customDateRange.end)) {
        return;
      }

      setIsLoading(true);
      setError(null);
      setStartPage(null);
      setDefaultStartPage(null);

      try {
        const dateRange = getCurrentDateRange();

        // Fetch site name
        const sites = await sitesApi.list();
        const site = sites.find(s => s.id === siteId);
        if (site) setSiteName(site.name);

        const [
          overviewData,
          timelineData,
          pagesData,
          referrersData,
          devicesData,
          browsersData,
          screensData,
          landingPagesData,
        ] = await Promise.all([
          analyticsApi.getOverview(siteId, dateRange),
          analyticsApi.getTimeline(siteId, dateRange),
          analyticsApi.getPages(siteId, dateRange, 10),
          analyticsApi.getReferrers(siteId, dateRange, 10),
          analyticsApi.getDevices(siteId, dateRange),
          analyticsApi.getBrowsers(siteId, dateRange, 10),
          analyticsApi.getScreens(siteId, dateRange),
          analyticsApi.getLandingPages(siteId, dateRange, 10),
        ]);

        const pickedStartPage = pickSankeyStartPage(landingPagesData);
        const sankey = await analyticsApi.getSankey(
          siteId,
          dateRange,
          sankeyRequestOptions(showExits, expanded, pickedStartPage)
        );

        setOverview(overviewData);
        setTimeline(timelineData);
        setPages(pagesData);
        setReferrers(referrersData);
        setDevices(devicesData);
        setBrowsers(browsersData);
        setScreens(screensData);
        setLandingPages(landingPagesData);
        setDefaultStartPage(pickedStartPage);
        setStartPage(pickedStartPage);
        setSankeyData(sankey);
      } catch (err) {
        console.error('Error fetching analytics:', err);
        setError('Failed to load analytics data');
      } finally {
        setIsLoading(false);
      }
    }

    fetchData();
  }, [siteId, dateRangeType, customDateRange.start, customDateRange.end]);

  async function refreshSankey(
    nextShowExits: boolean,
    nextExpanded: boolean,
    nextStartPage: string | null
  ) {
    if (!siteId) return;
    const sankey = await analyticsApi.getSankey(
      siteId,
      getCurrentDateRange(),
      sankeyRequestOptions(nextShowExits, nextExpanded, nextStartPage)
    );
    setSankeyData(sankey);
  }

  if (!siteId) {
    return <div className="p-8 text-center text-gray-500">No site selected</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link
                to={`/sites/${siteId}`}
                className="text-gray-500 hover:text-gray-700 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
                {siteName && <p className="text-sm text-gray-500">{siteName}</p>}
              </div>
            </div>

            {/* Date Range Selector */}
            <div className="flex items-center space-x-2">
              <Calendar className="w-5 h-5 text-gray-400" />
              <span className="text-sm text-gray-600">
                {formatDateRangeLabel(dateRangeType, customDateRange)}
              </span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Date Range Buttons */}
        <div className="mb-6">
          <div className="flex flex-wrap items-center gap-2">
            {DATE_RANGE_PRESETS.map(preset => (
              <button
                key={preset.days}
                onClick={() => {
                  setDateRangeType(String(preset.days) as DateRangeType);
                  setShowCustomDatePicker(false);
                }}
                className={cn(
                  'px-4 py-2 rounded-md text-sm font-medium transition-colors',
                  dateRangeType === String(preset.days)
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                )}
              >
                {preset.label}
              </button>
            ))}
            <button
              onClick={() => setShowCustomDatePicker(!showCustomDatePicker)}
              className={cn(
                'px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2 transition-colors',
                dateRangeType === 'custom'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
              )}
            >
              <Calendar className="w-4 h-4" />
              <span>Custom</span>
              <ChevronDown
                className={cn('w-4 h-4 transition-transform', showCustomDatePicker && 'rotate-180')}
              />
            </button>
          </div>

          {/* Custom Date Picker Panel */}
          {showCustomDatePicker && (
            <div className="mt-4 p-4 bg-white border border-gray-200 rounded-lg shadow-sm">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={customDateRange.start}
                    onChange={e => setCustomDateRange(prev => ({ ...prev, start: e.target.value }))}
                    max={new Date().toISOString().split('T')[0]}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                  <input
                    type="date"
                    value={customDateRange.end}
                    onChange={e => setCustomDateRange(prev => ({ ...prev, end: e.target.value }))}
                    min={customDateRange.start}
                    max={new Date().toISOString().split('T')[0]}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="flex items-end gap-2">
                  <button
                    onClick={() => {
                      if (customDateRange.start && customDateRange.end) {
                        setDateRangeType('custom');
                        setShowCustomDatePicker(false);
                      }
                    }}
                    disabled={!customDateRange.start || !customDateRange.end}
                    className={cn(
                      'flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors',
                      customDateRange.start && customDateRange.end
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    )}
                  >
                    Apply
                  </button>
                  <button
                    onClick={() => {
                      setShowCustomDatePicker(false);
                      if (dateRangeType === 'custom') {
                        setDateRangeType('30');
                      }
                    }}
                    className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : error ? (
          <div className="text-center py-20">
            <p className="text-red-600">{error}</p>
            <button
              onClick={() => setDateRangeType(dateRangeType)}
              className="mt-4 text-blue-600 hover:underline"
            >
              Try again
            </button>
          </div>
        ) : (
          <div className="space-y-8">
            {/* UTC Notice */}
            <p className="text-xs text-gray-400 text-right">All dates shown in UTC</p>

            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <KPICard
                title="Page Views"
                value={formatNumber(overview?.pageViews || 0)}
                previousValue={overview?.previousPageViews}
                currentValue={overview?.pageViews}
                icon={<BarChart2 className="w-6 h-6 text-blue-600" />}
              />
              <KPICard
                title="Sessions"
                value={formatNumber(overview?.sessions || 0)}
                previousValue={overview?.previousSessions}
                currentValue={overview?.sessions}
                icon={<Users className="w-6 h-6 text-blue-600" />}
              />
              <KPICard
                title="Avg Session"
                value={formatDuration(overview?.avgSessionDuration ?? null)}
                previousValue={overview?.previousAvgSessionDuration ?? undefined}
                currentValue={overview?.avgSessionDuration ?? undefined}
                icon={<Clock className="w-6 h-6 text-blue-600" />}
              />
              <KPICard
                title="Pages/Session"
                value={formatAvgPages(overview?.avgPagesPerSession ?? null)}
                previousValue={overview?.previousAvgPagesPerSession ?? undefined}
                currentValue={overview?.avgPagesPerSession ?? undefined}
                icon={<Layers className="w-6 h-6 text-blue-600" />}
              />
            </div>

            {/* Timeline Chart */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Page Views Over Time</h2>
              {timeline.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={timeline}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={value =>
                        new Date(value).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                        })
                      }
                    />
                    <YAxis />
                    <Tooltip
                      labelFormatter={value => new Date(value).toLocaleDateString()}
                      formatter={value => [formatNumber(value as number), 'Page Views']}
                    />
                    <Line
                      type="monotone"
                      dataKey="pageViews"
                      stroke="#3B82F6"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-center text-gray-500 py-12">No data available for this period</p>
              )}
            </div>

            <NavigationFlow
              data={sankeyData}
              showExits={showExits}
              expanded={expanded}
              isMobile={isMobile}
              startPage={startPage}
              onToggleExits={async () => {
                const next = !showExits;
                setShowExits(next);
                await refreshSankey(next, expanded, startPage);
              }}
              onToggleExpanded={async () => {
                const next = !expanded;
                setExpanded(next);
                await refreshSankey(showExits, next, startPage);
              }}
              onFocusPage={async pageId => {
                const next = pageId ?? defaultStartPage;
                setStartPage(next);
                await refreshSankey(showExits, expanded, next);
              }}
            />

            {/* Two Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Top Pages */}
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Top Pages</h2>
                {pages.length > 0 ? (
                  <div className="space-y-1">
                    {/* Header */}
                    <div className="flex items-center justify-between py-2 text-xs text-gray-500 border-b border-gray-200">
                      <span className="flex-1">Page</span>
                      <div className="flex items-center space-x-4">
                        <span className="w-16 text-right">Views</span>
                        <span
                          className="w-20 text-right"
                          title="% of visitors who entered on this page and left without viewing another page"
                        >
                          Bounce
                        </span>
                      </div>
                    </div>
                    {pages.map((page, idx) => (
                      <div
                        key={page.pagePath}
                        className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0"
                      >
                        <div className="flex items-center space-x-3 flex-1 min-w-0">
                          <span className="text-sm text-gray-400 w-6 flex-shrink-0">{idx + 1}</span>
                          <span className="text-sm text-gray-900 truncate">{page.pagePath}</span>
                        </div>
                        <div className="flex items-center space-x-4 text-sm flex-shrink-0">
                          <span className="text-gray-900 font-medium w-16 text-right">
                            {formatNumber(page.pageViews)}
                          </span>
                          <span
                            className={`w-20 text-right ${
                              page.bounceRate !== null && page.bounceRate > 0.7
                                ? 'text-red-600 font-medium'
                                : page.bounceRate !== null && page.bounceRate > 0.5
                                  ? 'text-amber-600'
                                  : 'text-gray-500'
                            }`}
                            title={`${page.bounces} bounces / ${page.entrances} entrances`}
                          >
                            {formatPercent(page.bounceRate)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-8">No page data available</p>
                )}
              </div>

              {/* Referrers */}
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Top Referrers</h2>
                {referrers.length > 0 ? (
                  <div className="space-y-3">
                    {referrers.map((referrer, idx) => (
                      <div
                        key={referrer.referrerHost}
                        className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0"
                      >
                        <div className="flex items-center space-x-3">
                          <span className="text-sm text-gray-400 w-6">{idx + 1}</span>
                          <span className="text-sm text-gray-900 truncate max-w-[200px]">
                            {referrer.referrerHost}
                          </span>
                        </div>
                        <span className="text-sm text-gray-900 font-medium">
                          {formatNumber(referrer.hits)}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-8">No referrer data available</p>
                )}
              </div>
            </div>

            {/* Top Landing Pages & Devices */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Top Landing Pages */}
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Globe className="w-5 h-5 text-blue-600" />
                  <h2 className="text-lg font-semibold text-gray-900">Top Landing Pages</h2>
                </div>
                <p className="text-xs text-gray-500 mb-3">Where visitors enter your site</p>
                {landingPages.length > 0 ? (
                  <div className="space-y-1">
                    {/* Header */}
                    <div className="flex items-center justify-between py-2 text-xs text-gray-500 border-b border-gray-200">
                      <span className="flex-1">Page</span>
                      <div className="flex items-center space-x-4">
                        <span className="w-16 text-right">Entrances</span>
                        <span className="w-16 text-right">Bounce</span>
                      </div>
                    </div>
                    {landingPages.map((landing, idx) => (
                      <div
                        key={landing.pagePath}
                        className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0"
                      >
                        <div className="flex items-center space-x-3 flex-1 min-w-0">
                          <span className="text-sm text-gray-400 w-6 flex-shrink-0">{idx + 1}</span>
                          <span className="text-sm text-gray-900 truncate">{landing.pagePath}</span>
                        </div>
                        <div className="flex items-center space-x-4 text-sm flex-shrink-0">
                          <span className="text-gray-900 font-medium w-16 text-right">
                            {formatNumber(landing.entrances)}
                          </span>
                          <span
                            className={`w-16 text-right ${
                              landing.bounceRate !== null && landing.bounceRate > 0.7
                                ? 'text-red-600 font-medium'
                                : landing.bounceRate !== null && landing.bounceRate > 0.5
                                  ? 'text-amber-600'
                                  : 'text-gray-500'
                            }`}
                          >
                            {formatPercent(landing.bounceRate)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-8">No landing page data available</p>
                )}
              </div>

              {/* Devices */}
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Devices</h2>
                {devices.length > 0 ? (
                  <div className="flex items-center justify-center">
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie
                          data={devices.map(d => ({ ...d, hits: toCount(d.hits) }))}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={80}
                          dataKey="hits"
                          nameKey="deviceType"
                        >
                          {devices.map((_, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value, name) => [formatNumber(value as number), name]}
                          labelFormatter={label => `${label}`}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-8">No device data available</p>
                )}
                {/* Device Legend with percentages */}
                {devices.length > 0 && (
                  <div className="mt-4 flex justify-center flex-wrap gap-4">
                    {devices.map((device, idx) => {
                      const total = devices.reduce((sum, d) => sum + toCount(d.hits), 0);
                      const percent =
                        total > 0 ? ((toCount(device.hits) / total) * 100).toFixed(0) : 0;
                      return (
                        <div key={device.deviceType} className="flex items-center space-x-2">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                          ></div>
                          <DeviceIcon type={device.deviceType} />
                          <span className="text-sm text-gray-600 capitalize">
                            {device.deviceType} ({percent}%)
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Browsers & Screen Sizes */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Browsers */}
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Chrome className="w-5 h-5 text-blue-600" />
                  <h2 className="text-lg font-semibold text-gray-900">Browsers</h2>
                </div>
                {browsers.length > 0 ? (
                  <div className="space-y-2">
                    {browsers.map((browser, idx) => {
                      const total = browsers.reduce((sum, b) => sum + toCount(b.hits), 0);
                      const percent = total > 0 ? (toCount(browser.hits) / total) * 100 : 0;
                      return (
                        <div key={browser.browser} className="flex items-center gap-3">
                          <span className="text-sm text-gray-400 w-6 flex-shrink-0">{idx + 1}</span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm text-gray-900 truncate">
                                {browser.browser}
                              </span>
                              <span className="text-sm text-gray-500 ml-2">
                                {percent.toFixed(0)}%
                              </span>
                            </div>
                            <div className="w-full bg-gray-100 rounded-full h-2">
                              <div
                                className="h-2 rounded-full"
                                style={{
                                  width: `${percent}%`,
                                  backgroundColor: COLORS[idx % COLORS.length],
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-8">No browser data available</p>
                )}
              </div>

              {/* Screen Sizes */}
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Monitor className="w-5 h-5 text-blue-600" />
                  <h2 className="text-lg font-semibold text-gray-900">Screen Sizes</h2>
                </div>
                {screens.length > 0 ? (
                  <div className="space-y-2">
                    {screens.map((screen, idx) => {
                      const total = screens.reduce((sum, s) => sum + toCount(s.hits), 0);
                      const percent = total > 0 ? (toCount(screen.hits) / total) * 100 : 0;
                      return (
                        <div key={screen.category} className="flex items-center gap-3">
                          <span className="text-sm text-gray-400 w-6 flex-shrink-0">{idx + 1}</span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm text-gray-900 truncate">
                                {screen.category}
                              </span>
                              <span className="text-sm text-gray-500 ml-2">
                                {percent.toFixed(0)}%
                              </span>
                            </div>
                            <div className="w-full bg-gray-100 rounded-full h-2">
                              <div
                                className="h-2 rounded-full"
                                style={{
                                  width: `${percent}%`,
                                  backgroundColor: COLORS[idx % COLORS.length],
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-8">No screen size data available</p>
                )}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default SiteAnalytics;
